import { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { GameGrid } from "@/components/game/GameGrid";
import { Keyboard } from "@/components/game/Keyboard";
import { ResultModal } from "@/components/game/ResultModal";
import { Tutorial } from "@/components/game/Tutorial";
import { Footer } from "@/components/layout/Footer";
import { FAQ } from "@/components/layout/FAQ";
import { AdSense } from "@/components/ads/AdSense";
import { CompactHeader } from "@/components/layout/CompactHeader";
import { SplashHeader } from "@/components/layout/SplashHeader";
import { SettingsDialog } from "@/components/game/SettingsDialog";
import { StatsDialog } from "@/components/game/StatsDialog";
import { useWormbleGame } from "@/hooks/useWormbleGame";
import { Button } from "@/components/ui/button";
import { isDateCompleted, isDateWon, saveDailyProgress } from "@/utils/gameProgress";
import { SEOHead } from "@/components/seo/SEOHead";
import { getWebApplicationSchema, getPuzzleArticleSchema } from "@/components/seo/structuredData";
import { InstallPrompt } from "@/components/pwa/InstallPrompt";
import { usePWANotifications } from "@/hooks/usePWANotifications";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { AuthGateModal } from "@/components/auth/AuthGateModal";
import { cleanupStaleData } from "@/utils/staleDataCleanup";
import { supabase } from "@/integrations/supabase/client";
import { FirstLoginInstallModal } from "@/components/pwa/FirstLoginInstallModal";
import { usePWA } from "@/hooks/usePWA";
import { InstallModal } from "@/components/pwa/InstallModal";
import { NotificationPermissionPrompt } from "@/components/pwa/NotificationPermissionPrompt";
import { useNotifications } from "@/hooks/useNotifications";
import { useUnifiedStats } from "@/hooks/useUnifiedStats";
import { WinCelebration } from "@/components/game/WinCelebration";
import { InGameShare } from "@/components/game/InGameShare";
import { WhatsNewModal } from "@/components/pwa/WhatsNewModal";
import { AnnouncementBanner } from "@/components/pwa/AnnouncementBanner";
import { CloudSyncIndicator } from "@/components/game/CloudSyncIndicator";
import { TooltipProvider } from "@/components/ui/tooltip";

const Play = () => {
  const { date: paramDate } = useParams();
  const [searchParams] = useSearchParams();
  
  // Enable PWA status notifications
  usePWANotifications();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  // Auth state
  const { isAuthenticated, loading: authLoading } = useAuth();
  const [hasSkippedAuth, setHasSkippedAuth] = useState(() => {
    return localStorage.getItem("wormble-skip-auth") === "true";
  });
  
  // PWA install modal state
  const { isInstalled } = usePWA();
  const [showFirstLoginInstall, setShowFirstLoginInstall] = useState(false);
  const [hasShownInstallPrompt, setHasShownInstallPrompt] = useState(() => {
    return localStorage.getItem("wormble-pwa-prompt-shown") === "true";
  });
  
  // Notification permission prompt state
  const { preferences: notificationPrefs, requestPermission } = useNotifications();
  const unifiedStats = useUnifiedStats();
  const [showNotificationPrompt, setShowNotificationPrompt] = useState(false);
  const [hasShownNotificationPrompt, setHasShownNotificationPrompt] = useState(() => {
    return localStorage.getItem("wormble-notification-prompt-shown") === "true";
  });
  
  // Mobile install banner state (24-hour reset)
  const [showMobileBanner, setShowMobileBanner] = useState(() => {
    if (isInstalled) return false;
    const dismissedTime = localStorage.getItem("wormble-mobile-banner-dismissed");
    if (!dismissedTime) return true;
    const hoursSinceDismiss = (Date.now() - parseInt(dismissedTime)) / (1000 * 60 * 60);
    return hoursSinceDismiss >= 24;
  });
  
  // Run stale data cleanup once on app load
  useEffect(() => {
    const runCleanup = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        const result = await cleanupStaleData(user?.id);
        
        if (result.cleaned) {
          const totalRemoved = result.localEntriesRemoved + result.cloudEntriesRemoved;
          toast({
            title: "Data Refresh Complete",
            description: `Removed ${totalRemoved} outdated puzzle${totalRemoved === 1 ? '' : 's'} to ensure accuracy.`,
          });
        }
      } catch (error) {
        console.error('Error during stale data cleanup:', error);
      }
    };
    
    runCleanup();
  }, []); // Run once on mount
  
  
  const getCurrentGameDate = (): Date => {
    if (paramDate) {
      const [y, m, d] = paramDate.split('-').map(Number);
      const local = new Date(y, (m || 1) - 1, d || 1);
      local.setHours(0, 0, 0, 0);
      if (!isNaN(local.getTime())) return local;
    }
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
  };

  const [gameDate, setGameDate] = useState(getCurrentGameDate);
  const { gameState, gameRows, addLetter, removeLetter, submitGuess, newGame, useHint, toggleHardMode, giveUp, isLoadingCloud, syncStatus } = useWormbleGame(gameDate);
  const [showTutorial, setShowTutorial] = useState(false);
  const [showResultModal, setShowResultModal] = useState(false);
  const [modalDismissed, setModalDismissed] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showStats, setShowStats] = useState(false);
  const [backgroundColor, setBackgroundColor] = useState("bg-background");
  const [showInstallModal, setShowInstallModal] = useState(false);
  const [showWinCelebration, setShowWinCelebration] = useState(false);
  
  // Auto-reveal solution toggle (default: true for backward compatibility)
  const [autoRevealSolution, setAutoRevealSolution] = useState(() => {
    const stored = localStorage.getItem("wormble-auto-reveal-solution");
    return stored === null ? true : stored === "true";
  });
  const [solutionRevealed, setSolutionRevealed] = useState(false);
  
  // Secret debug mode toggle (now controlled from SettingsDialog)
  const [secretDebugEnabled, setSecretDebugEnabled] = useState(() => {
    return localStorage.getItem("wormble-secret-debug") === "true";
  });

  // Update game date when URL parameter changes
  useEffect(() => {
    const newDate = getCurrentGameDate();
    if (newDate.toDateString() !== gameDate.toDateString()) {
      setGameDate(newDate);
      setShowResultModal(false); // Close modal when navigating to different date
      setSolutionRevealed(false); // Reset reveal state when date changes
      setModalDismissed(false); // Reset dismissal state when date changes
    }
  }, [paramDate]);

  // Reset modal dismissal state when date changes
  useEffect(() => {
    setModalDismissed(false);
  }, [gameDate]);

  // Show result modal when game ends (only if not already dismissed)
  useEffect(() => {
    if (gameState.gameOver && !modalDismissed) {
      // Show win celebration first for wins, then result modal
      if (gameState.gameResult === 'win') {
        setShowWinCelebration(true);
      } else {
        setShowResultModal(true);
      }
      
      // Show notification prompt after first win (aggressive strategy)
      if (
        gameState.gameResult === 'win' && 
        !hasShownNotificationPrompt && 
        !notificationPrefs?.pushEnabled &&
        isAuthenticated
      ) {
        // Delay to not overwhelm user with multiple modals
        setTimeout(() => {
          setShowNotificationPrompt(true);
          localStorage.setItem("wormble-notification-prompt-shown", "true");
          setHasShownNotificationPrompt(true);
        }, 3000); // Increased delay to account for celebration
      }
    } else if (!gameState.gameOver) {
      setShowResultModal(false);
      setShowWinCelebration(false);
      setModalDismissed(false); // Reset when game restarts
    }
  }, [gameState.gameOver, gameState.gameResult, modalDismissed, hasShownNotificationPrompt, notificationPrefs, isAuthenticated]);
  
  // Handle celebration complete
  const handleCelebrationComplete = () => {
    setShowWinCelebration(false);
    setShowResultModal(true);
  };

  // Load background color from localStorage
  useEffect(() => {
    const stored = localStorage.getItem("wormble-bg-color");
    if (stored) {
      setBackgroundColor(stored);
    }
  }, []);


  // Handle background color change
  const handleBackgroundColorChange = (color: string) => {
    setBackgroundColor(color);
    localStorage.setItem("wormble-bg-color", color);
  };


  // Handle auto-reveal solution toggle
  const handleAutoRevealToggle = () => {
    const newValue = !autoRevealSolution;
    setAutoRevealSolution(newValue);
    localStorage.setItem("wormble-auto-reveal-solution", String(newValue));
  };

  // Handle give up
  const handleGiveUp = () => {
    const solution = gameState.solution.toUpperCase();
    
    // Call the giveUp function from the hook
    giveUp();
    
    // Close settings and show result modal
    setShowSettings(false);
    setShowResultModal(true);
    
    toast({
      title: "Game Over",
      description: `The word was ${solution}`,
      variant: "destructive",
    });
  };

  // Handle reset settings
  const handleResetSettings = () => {
    // Clear preference keys only (not game data)
    localStorage.removeItem('wormble-bg-color');
    localStorage.removeItem('wormble-auto-reveal-solution');
    localStorage.removeItem('theme');
    
    // Reset UI state to defaults
    setBackgroundColor('bg-background');
    setAutoRevealSolution(true);
    
    // Reset theme to system default
    document.documentElement.classList.remove('light', 'dark');
    
    toast({
      title: "Settings Reset",
      description: "All preferences have been reset to defaults",
    });
  };

  // Date navigation helpers
  const formatDateForUrl = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const formatDateForDisplay = (date: Date): string => {
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    if (date.toDateString() === today.toDateString()) {
      return date.toLocaleDateString('en-US', { 
        weekday: 'short', 
        month: 'short', 
        day: 'numeric' 
      });
    } else if (date.toDateString() === yesterday.toDateString()) {
      return "Yesterday";
    } else {
      return date.toLocaleDateString('en-US', { 
        weekday: 'short', 
        month: 'short', 
        day: 'numeric' 
      });
    }
  };

  const getPuzzleNumber = (date: Date): number => {
    const epochDate = new Date(2024, 8, 1);
    const daysSinceEpoch = Math.floor((date.getTime() - epochDate.getTime()) / (1000 * 60 * 60 * 24));
    return Math.max(1, daysSinceEpoch + 1);
  };

  // SEO data
  const puzzleNumber = getPuzzleNumber(gameDate);
  const formattedDateDisplay = formatDateForDisplay(gameDate);
  const urlDate = formatDateForUrl(gameDate);
  const today = new Date();
  const isTodayDate = gameDate.toDateString() === today.toDateString();
  
  const seoTitle = isTodayDate 
    ? "Wormble - Daily Word Puzzle Game" 
    : `Wormble Puzzle #${puzzleNumber} - ${formattedDateDisplay}`;
  const seoDescription = isTodayDate
    ? "Guess the hidden 5-letter word in 5 tries. New puzzle daily!"
    : `Play Wormble puzzle #${puzzleNumber} from ${formattedDateDisplay}. Guess the 5-letter word in 5 tries.`;
  const canonicalUrl = isTodayDate ? "/play" : `/puzzle/${urlDate}`;
  const structuredData = isTodayDate 
    ? getWebApplicationSchema()
    : getPuzzleArticleSchema(puzzleNumber, urlDate);

  // Navigation functions
  const goToPreviousDay = () => {
    const prevDay = new Date(gameDate);
    prevDay.setDate(prevDay.getDate() - 1);
    
    // Don't go before epoch date
     const epochDate = new Date(2024, 8, 1);
    if (prevDay >= epochDate) {
      setShowResultModal(false); // Close modal before navigation
      navigate(`/puzzle/${formatDateForUrl(prevDay)}`);
    }
  };

  const goToNextDay = () => {
    const nextDay = new Date(gameDate);
    nextDay.setDate(nextDay.getDate() + 1);
    
    // Don't go beyond today
    const today = new Date();
    if (nextDay <= today) {
      setShowResultModal(false); // Close modal before navigation
      navigate(`/puzzle/${formatDateForUrl(nextDay)}`);
    }
  };

  const goToToday = () => {
    setShowResultModal(false); // Close modal before navigation
    navigate('/play');
  };

  const goToArchive = () => {
    setShowResultModal(false); // Close modal before navigation
    navigate('/archive');
  };

  // Check if navigation is available
  const canGoPrevious = () => {
    const prevDay = new Date(gameDate);
    prevDay.setDate(prevDay.getDate() - 1);
    const epochDate = new Date(2024, 8, 1);
    return prevDay >= epochDate;
  };

  const canGoNext = () => {
    const nextDay = new Date(gameDate);
    nextDay.setDate(nextDay.getDate() + 1);
    const today = new Date();
    return nextDay <= today;
  };

  const isToday = () => {
    const today = new Date();
    return gameDate.toDateString() === today.toDateString();
  };

  // Handle continue as guest
  const handleContinueAsGuest = () => {
    localStorage.setItem("wormble-skip-auth", "true");
    setHasSkippedAuth(true);
    
    // Show first-login install modal after continuing as guest
    if (!isInstalled && !hasShownInstallPrompt) {
      setTimeout(() => setShowFirstLoginInstall(true), 500);
    }
  };
  
  // Show first-login install modal after first WIN (smart timing)
  useEffect(() => {
    const hasShownThisSession = sessionStorage.getItem("wormble-pwa-shown-this-session") === "true";
    
    // Show after first win, only once per session
    if (!isInstalled && !hasShownThisSession && gameState.gameOver && gameState.gameResult === "win") {
      setShowFirstLoginInstall(true);
      sessionStorage.setItem("wormble-pwa-shown-this-session", "true");
    }
  }, [isInstalled, gameState.gameOver, gameState.gameResult]);
  
  // Handle first-login install modal close
  const handleFirstLoginInstallClose = () => {
    setShowFirstLoginInstall(false);
  };
  
  // Handle mobile banner dismiss
  const handleMobileBannerDismiss = () => {
    setShowMobileBanner(false);
    localStorage.setItem("wormble-mobile-banner-dismissed", String(Date.now()));
  };

  // Determine if auth gate should be shown
  const shouldShowAuthGate = !authLoading && !isAuthenticated && !hasSkippedAuth;

  // Handle debug mode toggle (triggered from SettingsDialog)
  const handleDebugToggle = () => {
    const newDebugState = !secretDebugEnabled;
    setSecretDebugEnabled(newDebugState);
    localStorage.setItem("wormble-secret-debug", String(newDebugState));
    
    toast({
      title: newDebugState ? "Debug Mode Enabled" : "Debug Mode Disabled",
      description: newDebugState ? "Access debug panel in settings" : "Debug panel hidden",
    });
  };

  // Handle physical keyboard input
  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (gameState.gameOver) return;
      
      const key = event.key.toLowerCase();
      
      if (key === "enter") {
        event.preventDefault();
        submitGuess();
      } else if (key === "backspace") {
        event.preventDefault();
        removeLetter();
      } else if (/^[a-z]$/.test(key)) {
        event.preventDefault();
        addLetter(key);
      }
    };

    window.addEventListener("keydown", handleKeyPress);
    return () => window.removeEventListener("keydown", handleKeyPress);
  }, [gameState.gameOver, addLetter, removeLetter, submitGuess]);

  // Initialize AdSense ads
  useEffect(() => {
    try {
      interface WindowWithAdsbygoogle extends Window {
        adsbygoogle?: Array<Record<string, unknown>>;
      }
      const win = window as WindowWithAdsbygoogle;
      if (typeof window !== 'undefined' && win.adsbygoogle) {
        (win.adsbygoogle = win.adsbygoogle || []).push({});
      }
    } catch (err) {
      // AdSense initialization failed silently
    }
  }, []);

  return (
    <>
      <SEOHead 
        title={seoTitle}
        description={seoDescription}
        canonicalUrl={canonicalUrl}
        ogImage="/og/wormble-share.png"
        type={isTodayDate ? "website" : "article"}
        structuredData={structuredData}
        puzzleNumber={puzzleNumber}
        guessCount={gameState.gameOver ? gameState.guesses.length : undefined}
      />
      
      {/* Auth Gate Modal */}
      <AuthGateModal 
        isOpen={shouldShowAuthGate}
        onContinueAsGuest={handleContinueAsGuest}
      />
      
      {/* What's New Modal - triggered on version update */}
      <WhatsNewModal />
      
      {/* Announcement Banner - dismissible */}
      <AnnouncementBanner />
      
      <div className={`min-h-[100dvh] flex flex-col overflow-x-hidden ${backgroundColor}`}>
      {/* Screen reader only H1 for accessibility */}
      <h1 className="sr-only">Wormble Daily Word Puzzle #{puzzleNumber}</h1>
      
      {/* Compact Header */}
      <CompactHeader
        puzzleNumber={getPuzzleNumber(gameDate)}
        gameDate={gameDate}
        isCompleted={isDateCompleted(gameDate)}
        isWon={isDateWon(gameDate)}
        onSettingsClick={() => setShowSettings(true)}
        onArchiveClick={() => navigate('/archive')}
        onTitleClick={() => {}}
        onHelpClick={() => setShowTutorial(true)}
        debugEnabled={secretDebugEnabled}
        isArchivePuzzle={!isToday()}
        onInstallClick={!isInstalled ? () => setShowInstallModal(true) : undefined}
        onStatsClick={() => setShowStats(true)}
      />

      {/* Main Game Column - Content at top */}
      <main className="flex flex-col justify-start py-4 px-2 md:landscape:overflow-y-auto md:landscape:scrollbar-thin">
        {/* Splash Header - Only show for guests/unauthenticated users */}
        {!isAuthenticated && (
          <SplashHeader
            puzzleNumber={getPuzzleNumber(gameDate)}
            formattedDate={formatDateForDisplay(gameDate)}
            hasGuessed={gameState.guesses.length > 0}
            onHowToPlayClick={() => setShowTutorial(true)}
            onBlogClick={() => navigate('/blog')}
            onInstallClick={() => setShowInstallModal(true)}
          />
        )}

        {/* Message Display */}
        {gameState.message && (
          <div className="w-full max-w-[350px] mx-auto text-center p-2 sm:p-3 bg-muted rounded-lg animate-fade-in mb-2">
            <p className="text-xs sm:text-sm font-medium">{gameState.message}</p>
          </div>
        )}

        {/* Game Grid */}
        <div className="w-full max-w-[350px] mx-auto mb-4 relative" data-testid="game-grid-container" data-game-area="true">
          {/* Loading overlay for cloud data */}
          {isLoadingCloud && (
            <div className="absolute inset-0 bg-background/50 flex items-center justify-center z-10 rounded-lg backdrop-blur-sm">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <div className="h-4 w-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                <span>Loading from cloud...</span>
              </div>
            </div>
          )}
          
          <GameGrid 
            rows={gameRows}
            currentGuess={gameState.currentGuess}
            currentRowIndex={gameState.guesses.length}
            solution={gameState.solution}
            showSolution={gameState.gameOver && gameState.gameResult === "lose" && (autoRevealSolution || solutionRevealed)}
            showRevealButton={gameState.gameOver && gameState.gameResult === "lose" && !autoRevealSolution && !solutionRevealed}
            onRevealClick={() => setSolutionRevealed(true)}
          />
          
          {/* Cloud Sync Indicator + In-Game Share */}
          {!gameState.gameOver && gameState.guesses.length > 0 && (
            <TooltipProvider>
              <div className="flex items-center justify-center gap-4 mt-2">
                <CloudSyncIndicator 
                  isLoading={isLoadingCloud}
                  syncStatus={syncStatus}
                  isAuthenticated={isAuthenticated}
                />
                <InGameShare
                  puzzleNumber={puzzleNumber}
                  guessCount={gameState.guesses.length}
                  results={gameState.results.map(row => 
                    row.map(status => status === "correct" ? "correct" : status === "present" ? "present" : "absent")
                  )}
                />
              </div>
            </TooltipProvider>
          )}
          
          {/* Show sync indicator when game is over but user is authenticated */}
          {gameState.gameOver && isAuthenticated && (
            <TooltipProvider>
              <div className="flex justify-center mt-2">
                <CloudSyncIndicator 
                  isLoading={isLoadingCloud}
                  syncStatus={syncStatus}
                  isAuthenticated={isAuthenticated}
                />
              </div>
            </TooltipProvider>
          )}
        </div>
      </main>

      {/* FAQ Section - Hidden on mobile for cleaner UI */}
      <div className="hidden md:block">
        <FAQ />
      </div>

      {/* On-screen Keyboard - Positioned at bottom */}
      <div className="pb-2" data-game-keyboard-container="true">
        <div className="w-full sm:max-w-[350px] sm:mx-auto" data-testid="keyboard-container">
          <Keyboard
            keyStatuses={gameState.keyStatuses}
            onKeyPress={addLetter}
            onEnter={submitGuess}
            onBackspace={removeLetter}
            disabled={gameState.gameOver}
          />
        </div>
      </div>

      {/* Persistent Mobile Install Banner (above ads) */}
      {!isInstalled && showMobileBanner && (
        <div className="w-full max-w-[350px] mx-auto px-2 mb-2 sm:hidden">
          <div className="bg-primary/10 border border-primary/30 rounded-lg p-3 flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <span className="text-lg">📱</span>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-foreground">Add Wormble to Home Screen</p>
                <p className="text-[10px] text-foreground/80 truncate" aria-hidden="true">Never miss a puzzle</p>
              </div>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <button
                onClick={() => setShowInstallModal(true)}
                className="text-xs text-primary hover:underline font-medium px-2 py-1"
              >
                Install
              </button>
              <button
                onClick={handleMobileBannerDismiss}
                className="text-xs text-muted-foreground hover:text-foreground px-2 py-1"
                aria-label="Dismiss"
              >
                ✕
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bottom Section - Ad Banner + Footer */}
      <div className="mt-auto">
        <div className="px-2">
          <div className="w-full max-w-[350px] mx-auto">
            <AdSense 
              slot="4490025568"
              size="mobile-banner-320x100"
              hideOnMobile={false}
            />
          </div>
        </div>

        {/* Footer - Now visible on mobile */}
        <Footer />
      </div>

      {/* Win Celebration Animation */}
      {showWinCelebration && (
        <WinCelebration 
          solution={gameState.solution}
          onComplete={handleCelebrationComplete}
        />
      )}

      {/* Result Modal */}
      <ResultModal
        isOpen={showResultModal}
        result={gameState.gameResult!}
        solution={gameState.solution}
        guesses={gameState.guesses}
        results={gameState.results.map(row => 
          row.map(status => status === "correct" ? "correct" : status === "present" ? "present" : "absent")
        )}
        onPlayAgain={() => {
          setShowResultModal(false);
          newGame(gameDate);
        }}
        onClose={() => {
          setShowResultModal(false);
          setModalDismissed(true);
        }}
        isAlreadyPlayed={gameState.gameOver && isDateCompleted(gameDate)}
        puzzleNumber={puzzleNumber}
        onShowInstallPrompt={!isInstalled && !hasShownInstallPrompt ? () => setShowInstallModal(true) : undefined}
        hintsUsed={gameState.hintsUsed}
      />

      {/* Tutorial Modal */}
      <Tutorial
        isOpen={showTutorial}
        onClose={() => setShowTutorial(false)}
      />

      {/* Settings Dialog */}
      <SettingsDialog
        open={showSettings}
        onOpenChange={setShowSettings}
        hardMode={gameState.hardMode}
        onHardModeToggle={toggleHardMode}
        hardModeDisabled={!gameState.gameOver && gameState.guesses.length > 0}
        backgroundColor={backgroundColor}
        onBackgroundColorChange={handleBackgroundColorChange}
        autoRevealSolution={autoRevealSolution}
        onAutoRevealToggle={handleAutoRevealToggle}
        solution={gameState.solution}
        guesses={gameState.guesses}
        onHintUsed={(position, letter) => useHint(position, letter)}
        hintsUsed={gameState.hintsUsed}
        onGiveUp={handleGiveUp}
        gameOver={gameState.gameOver}
        gameState={gameState}
        showDebug={searchParams.get('debug') === 'true' || secretDebugEnabled}
        onResetSettings={handleResetSettings}
        onDebugToggle={handleDebugToggle}
        onInstallClick={!isInstalled ? () => setShowInstallModal(true) : undefined}
      />

      {/* Stats Dialog */}
      <StatsDialog
        open={showStats}
        onOpenChange={setShowStats}
        puzzleNumber={puzzleNumber}
      />

      {/* PWA Install Prompt */}
      <InstallPrompt />
      
      {/* First-Login Install Modal */}
      <FirstLoginInstallModal 
        isOpen={showFirstLoginInstall}
        onClose={handleFirstLoginInstallClose}
      />
      
      {/* Install Modal */}
      <InstallModal 
        open={showInstallModal}
        onOpenChange={setShowInstallModal}
        isOnline={navigator.onLine}
      />
      
      {/* Notification Permission Prompt */}
      <NotificationPermissionPrompt
        open={showNotificationPrompt}
        onOpenChange={setShowNotificationPrompt}
        onRequestPermission={requestPermission}
        streakCount={unifiedStats.currentStreak || 0}
      />
    </div>
    </>
  );
};

export default Play;
